<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { VideoItem } from '@/types'
import VideoModal from '@/components/VideoModal.vue'
import { captureVideoFrame } from '@/lib/videoThumbnail'

const props = defineProps<{
	videos: VideoItem[]
	columns: number
}>()

const activeVideo = ref<VideoItem | null>(null)

// Posts without a featured image get a thumbnail auto-captured client-side
// from a frame of their own video (self-hosted files only — no server-side
// video processing required). Filled in asynchronously per card.
const capturedThumbnails = ref<Record<number, string>>({})

function thumbnailFor(video: VideoItem): string {
	return video.thumbnail || capturedThumbnails.value[video.id] || ''
}

onMounted(() => {
	props.videos
		.filter(v => !v.thumbnail)
		.forEach(video => {
			captureVideoFrame(video.videoUrl).then(frame => {
				if (frame) {
					capturedThumbnails.value = { ...capturedThumbnails.value, [video.id]: frame }
				}
			})
		})
})

// Static class map so Tailwind's build-time scanner can see every
// possible combination (dynamic template strings would be invisible to it).
const columnClasses: Record<number, string> = {
	1: 'vg:grid-cols-1',
	2: 'vg:grid-cols-1 sm:vg:grid-cols-2',
	3: 'vg:grid-cols-1 sm:vg:grid-cols-2 lg:vg:grid-cols-3',
	4: 'vg:grid-cols-1 sm:vg:grid-cols-2 lg:vg:grid-cols-4',
}

const gridColsClass = computed(() => {
	const cols = props.columns >= 1 && props.columns <= 4 ? props.columns : 2
	return columnClasses[cols]
})

function openVideo(video: VideoItem) {
	activeVideo.value = video
}

function closeVideo() {
	activeVideo.value = null
}
</script>

<template>
	<div class="vg:mx-auto vg:w-full vg:max-w-6xl vg:px-4 vg:py-8 sm:vg:px-8">
		<div v-if="!props.videos.length" class="vg:text-center vg:text-gray-500">
			No videos to display yet.
		</div>

		<div v-else class="vg:grid vg:gap-6" :class="gridColsClass">
			<button
				v-for="video in props.videos"
				:key="video.id"
				type="button"
				@click="openVideo(video)"
				class="vg:group vg:flex vg:flex-col vg:overflow-hidden vg:rounded-lg vg:border vg:border-gray-200 vg:bg-white vg:text-left vg:shadow-sm vg:transition hover:vg:shadow-lg focus:vg:outline-none focus-visible:vg:ring-2 focus-visible:vg:ring-offset-2 focus-visible:vg:ring-red-500"
			>
				<div class="vg:relative vg:aspect-video vg:w-full vg:overflow-hidden vg:bg-gray-900">
					<img
						v-if="thumbnailFor(video)"
						:src="thumbnailFor(video)"
						:alt="video.title"
						class="vg:h-full vg:w-full vg:object-cover vg:transition vg:duration-200 group-hover:vg:scale-105"
					/>
					<div
						class="vg:absolute vg:inset-0 vg:flex vg:items-center vg:justify-center vg:bg-black/25 vg:transition group-hover:vg:bg-black/40"
					>
						<span
							class="vg:flex vg:h-14 vg:w-14 vg:items-center vg:justify-center vg:rounded-full vg:bg-white/90 vg:text-red-600 vg:shadow-md"
							aria-hidden="true"
						>
							<svg viewBox="0 0 24 24" fill="currentColor" class="vg:h-6 vg:w-6 vg:ml-0.5">
								<path d="M8 5v14l11-7z" />
							</svg>
						</span>
					</div>
				</div>

				<div class="vg:flex vg:flex-1 vg:flex-col vg:gap-2 vg:p-4">
					<h3 class="vg:text-lg vg:font-semibold vg:text-gray-900">{{ video.title }}</h3>
					<p v-if="video.excerpt" class="vg:line-clamp-3 vg:text-sm vg:text-gray-600">{{ video.excerpt }}</p>
				</div>
			</button>
		</div>

		<VideoModal v-if="activeVideo" :video="activeVideo" @close="closeVideo" />
	</div>
</template>
